import React, { useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import {
  DownOutlined,
  FileTextOutlined,
  HomeOutlined,
  MailOutlined,
  PlayCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import logo from '../assets/Logo_wordmark_lightbg.svg';
import { buildSignupPath, buildSignupState, captureCtaClick } from '../utils/analytics';

const resourceMenuItems = [
  {
    key: 'clinical-guides-updates',
    icon: <FileTextOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources"
        aria-label="Clinical guides and updates"
      >
        <span className="site-glp1-bridge-menu-title">Clinical guides and updates</span>
        <span className="site-glp1-bridge-menu-copy">
          Browse guides, resource centers, recordings, and clinical tools.
        </span>
      </Link>
    ),
  },
  {
    key: 'newsletter',
    icon: <MailOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources/newsletter"
        aria-label="MedHug Newsletter archive"
      >
        <span className="site-glp1-bridge-menu-title">Newsletter</span>
        <span className="site-glp1-bridge-menu-copy">
          Read every monthly MedHug Newsletter issue.
        </span>
      </Link>
    ),
  },
];

const glp1BridgeMenuItems = [
  {
    key: 'hub',
    icon: <HomeOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources/glp1-bridge"
        aria-label="GLP-1 Bridge resource hub"
      >
        <span className="site-glp1-bridge-menu-title">Resource hub</span>
        <span className="site-glp1-bridge-menu-copy">Choose assistant, recording, or guide.</span>
      </Link>
    ),
  },
  {
    key: 'assistant',
    icon: <SafetyCertificateOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources/glp1-bridge/assistant"
        aria-label="GLP-1 Bridge screening tool and AI assistant"
      >
        <span className="site-glp1-bridge-menu-title">Screening tool + AI</span>
        <span className="site-glp1-bridge-menu-copy">Use the provider-gated workflow assistant.</span>
      </Link>
    ),
  },
  {
    key: 'recording',
    icon: <PlayCircleOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources/glp1-bridge/recording"
        aria-label="GLP-1 Bridge recording and slides"
      >
        <span className="site-glp1-bridge-menu-title">Recording + slides</span>
        <span className="site-glp1-bridge-menu-copy">Watch the walkthrough and review the deck.</span>
      </Link>
    ),
  },
  {
    key: 'guide',
    icon: <FileTextOutlined aria-hidden="true" />,
    label: (
      <Link
        className="site-glp1-bridge-menu-link"
        to="/resources/glp1-bridge/guide"
        aria-label="GLP-1 Bridge clinical guide"
      >
        <span className="site-glp1-bridge-menu-title">Clinical guide</span>
        <span className="site-glp1-bridge-menu-copy">Read eligibility, billing, and CMS source links.</span>
      </Link>
    ),
  },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-sm'
          : 'bg-white border-b border-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="flex items-center justify-between h-[52px]">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="MedHug" className="h-10 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <nav className="flex items-center gap-7">
              <Link to="/about" className="text-[#1d1d1f] text-sm hover:text-[#6e6e73] transition-colors">
                About Us
              </Link>
              <Dropdown
                menu={{ items: resourceMenuItems }}
                overlayClassName="site-glp1-bridge-menu"
                placement="bottomRight"
                trigger={['hover']}
                mouseEnterDelay={0.08}
                mouseLeaveDelay={0.16}
              >
                <Link
                  to="/resources"
                  className="site-glp1-bridge-trigger text-[#1d1d1f] text-sm hover:text-[#6e6e73] transition-colors"
                >
                  Resources
                  <DownOutlined className="site-glp1-bridge-trigger-caret" aria-hidden="true" />
                </Link>
              </Dropdown>
              <Link to="/news" className="text-[#1d1d1f] text-sm hover:text-[#6e6e73] transition-colors">
                Latest News
              </Link>
              <Dropdown
                menu={{ items: glp1BridgeMenuItems }}
                overlayClassName="site-glp1-bridge-menu"
                placement="bottomRight"
                trigger={['hover']}
                mouseEnterDelay={0.08}
                mouseLeaveDelay={0.16}
              >
                <Link
                  to="/resources/glp1-bridge"
                  className="site-glp1-bridge-trigger text-[#1d1d1f] text-sm hover:text-[#6e6e73] transition-colors"
                >
                  GLP-1 Bridge
                  <DownOutlined className="site-glp1-bridge-trigger-caret" aria-hidden="true" />
                </Link>
              </Dropdown>
            </nav>

            <Link
              to={buildSignupPath()}
              state={buildSignupState('header-signup')}
              data-ph-capture-attribute-cta="header-signup"
              onClick={() => captureCtaClick('header-signup', { placement: 'header-desktop' })}
              className="bg-[#1d1d1f] px-4 py-[6px] text-sm text-white hover:bg-[#333] transition-all rounded-full"
            >
              Sign Up
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-[#1d1d1f]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl px-8 py-6 space-y-4 border-t border-black/5">
          <Link to="/about" className="block text-[#1d1d1f] text-sm py-2" onClick={() => setMobileOpen(false)}>
            About Us
          </Link>
          <div>
            <Link to="/resources" className="block text-[#1d1d1f] text-sm py-2" onClick={() => setMobileOpen(false)}>Resources</Link>
            <div className="ml-4 mt-1 space-y-1 border-l border-black/10 pl-4">
              <Link
                to="/resources"
                aria-label="Clinical guides and updates"
                className="block text-[#475467] text-sm py-3"
                onClick={() => setMobileOpen(false)}
              >
                Clinical guides and updates
              </Link>
              <Link
                to="/resources/newsletter"
                aria-label="MedHug Newsletter archive"
                className="block text-[#475467] text-sm py-3"
                onClick={() => setMobileOpen(false)}
              >
                Newsletter
              </Link>
            </div>
          </div>
          <Link to="/news" className="block text-[#1d1d1f] text-sm py-2" onClick={() => setMobileOpen(false)}>Latest News</Link>
          <div>
            <Link to="/resources/glp1-bridge" className="block text-[#1d1d1f] text-sm py-2" onClick={() => setMobileOpen(false)}>GLP-1 Bridge</Link>
            <div className="ml-4 mt-1 space-y-1 border-l border-black/10 pl-4">
              <Link to="/resources/glp1-bridge/assistant" className="block text-[#475467] text-sm py-1.5" onClick={() => setMobileOpen(false)}>Screening tool + AI</Link>
              <Link to="/resources/glp1-bridge/recording" className="block text-[#475467] text-sm py-1.5" onClick={() => setMobileOpen(false)}>Recording + slides</Link>
              <Link to="/resources/glp1-bridge/guide" className="block text-[#475467] text-sm py-1.5" onClick={() => setMobileOpen(false)}>Clinical guide</Link>
            </div>
          </div>
          <div className="pt-2">
            <Link
              to={buildSignupPath()}
              state={buildSignupState('header-signup-mobile')}
              data-ph-capture-attribute-cta="header-signup-mobile"
              onClick={() => {
                setMobileOpen(false);
                captureCtaClick('header-signup-mobile', { placement: 'header-mobile' });
              }}
              className="block text-center bg-[#1d1d1f] px-5 py-3 text-sm text-white rounded-full"
            >
              Sign Up
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
